/*
 * WebSerial concrete DFU transport. Replaces both DfuTransportSerial (the
 * J-Link / FTDI variant) and DfuTransportUsbSerial (the Nordic USB CDC ACM
 * variant) from pc-nrf-dfu-js.
 *
 * The Open USB Bootloader (VID 0x1915 / PID 0x521f) presents a CDC ACM
 * interface; macOS / Windows / Linux all expose this as a serial port that
 * WebSerial can claim. After the firmware image is fully sent, the bootloader
 * resets and the OS tears the port down — we treat reader.read() returning
 * done=true (or a NetworkError on writes/reads) as "transfer complete".
 */

import * as slip from "../util/slip.js";
import { DfuTransportPrn } from "./prn-transport.js";
import { DfuError, ErrorCode } from "./errors.js";

const DEFAULT_PRN = 16;

export interface WebSerialTransportOptions {
  prn?: number;
}

export class WebSerialDfuTransport extends DfuTransportPrn {
  private readonly port: SerialPort;
  private decoder: slip.Decoder | undefined;

  private writer: WritableStreamDefaultWriter<Uint8Array> | undefined;
  private reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  private readPumpPromise: Promise<void> | undefined;

  private readyPromise: Promise<void> | undefined;
  private disconnected = false;

  constructor(port: SerialPort, opts: WebSerialTransportOptions = {}) {
    super(opts.prn ?? DEFAULT_PRN);
    this.port = port;
  }

  /** Background pump: reads bytes from the port, feeds them through the SLIP decoder. */
  private async runReadPump(): Promise<void> {
    if (!this.port.readable) {
      throw new DfuError(ErrorCode.ERROR_PORT_NOT_OPEN, "port.readable is null");
    }
    this.reader = this.port.readable.getReader();
    try {
      while (true) {
        const { value, done } = await this.reader.read();
        if (done) {
          this.disconnected = true;
          this.onTransportClosed();
          return;
        }
        if (value && value.byteLength > 0 && this.decoder) {
          this.decoder.decode(value);
        }
      }
    } catch (err) {
      this.disconnected = true;
      this.onTransportClosed();
      // Swallow — the bootloader's expected disconnect at end-of-flash
      // surfaces here as a NetworkError, which is fine.
      if (err instanceof Error && err.name !== "NetworkError") {
        // Non-disconnect errors are unusual; log to console for diagnostic context.
        console.warn("[dfu] read pump error:", err);
      }
    } finally {
      try {
        this.reader?.releaseLock();
      } catch {
        // ignore
      }
      this.reader = undefined;
    }
  }

  protected override async writeCommand(bytes: Uint8Array): Promise<void> {
    await this.ensureOpen();
    if (!this.writer) {
      throw new DfuError(ErrorCode.ERROR_PORT_NOT_OPEN, "writer not available");
    }
    let encoded = slip.encode(bytes);
    // Strip the leading 0xC0 — the nRF SDK SLIP impl rejects back-to-back END markers.
    encoded = encoded.subarray(1);
    await this.writer.write(encoded);
  }

  protected override async writeData(bytes: Uint8Array): Promise<void> {
    // Wrap raw payload in a 0x08 'Write' command.
    const cmd = new Uint8Array(bytes.length + 1);
    cmd[0] = 0x08;
    cmd.set(bytes, 1);
    await this.writeCommand(cmd);
  }

  private async ensureOpen(): Promise<void> {
    if (this.disconnected) {
      throw new DfuError(ErrorCode.ERROR_DEVICE_DISCONNECTED);
    }
    if (!this.port.readable || !this.port.writable) {
      throw new DfuError(ErrorCode.ERROR_PORT_NOT_OPEN);
    }
    if (!this.writer) {
      this.writer = this.port.writable.getWriter();
    }
    if (!this.decoder) {
      this.decoder = new slip.Decoder((msg) => this.onData(msg));
    }
    if (!this.readPumpPromise) {
      this.readPumpPromise = this.runReadPump();
    }
  }

  /** Performs the one-time DFU handshake: set PRN, request MTU. */
  protected override ready(): Promise<void> {
    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = this.handshake();
    return this.readyPromise;
  }

  private async handshake(): Promise<void> {
    // Set PRN
    await this.writeCommand(new Uint8Array([0x02, this.prn & 0xff, (this.prn >> 8) & 0xff]));
    this.assertPacket(0x02, 0, await this.read());

    // Request MTU
    await this.writeCommand(new Uint8Array([0x07]));
    const mtuBytes = this.assertPacket(0x07, 2, await this.read());
    const wireMtu = mtuBytes[0] | (mtuBytes[1] << 8);

    // wireMtu is the max number of SLIP-encoded bytes per write cmd.
    // /2 accounts for worst-case SLIP doubling; -1 for trailing END; -1 for the 0x08 opcode.
    let payloadMtu = Math.floor(wireMtu / 2) - 2;
    payloadMtu -= payloadMtu % 4; // 4-byte align to keep flash writes happy
    if (payloadMtu <= 0) {
      throw new DfuError(ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES, `Bad MTU ${wireMtu}`);
    }
    this.mtu = payloadMtu;
  }

  /**
   * Wait for the bootloader to disconnect (signals firmware was accepted and
   * the device is rebooting into the application). Resolves when the port
   * goes away, or after a timeout.
   */
  async waitForDisconnect(timeoutMs = 5000): Promise<void> {
    if (this.disconnected) return;
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(), timeoutMs);
      const onDisconnect = (): void => {
        clearTimeout(timer);
        this.port.removeEventListener("disconnect", onDisconnect);
        resolve();
      };
      this.port.addEventListener("disconnect", onDisconnect);
      // Also poll the pump promise — it resolves when reader hits EOF.
      this.readPumpPromise?.then(() => {
        clearTimeout(timer);
        this.port.removeEventListener("disconnect", onDisconnect);
        resolve();
      });
    });
  }

  /** Release locks and close the port. Safe to call multiple times. */
  async close(): Promise<void> {
    try {
      if (this.writer) {
        try {
          this.writer.releaseLock();
        } catch {
          // ignore
        }
        this.writer = undefined;
      }
      if (this.reader) {
        try {
          await this.reader.cancel();
        } catch {
          // ignore
        }
      }
      await this.readPumpPromise?.catch(() => undefined);
    } finally {
      try {
        await this.port.close();
      } catch {
        // ignore
      }
    }
  }
}
